(() => {
  'use strict';

  const PREFIX = '[FTR Cart Page Bundles]';
  const ROOT_SELECTOR = '.cc-main-cart cart-items';
  const GENERATED_CLASS = 'ftr-loop-bundle-header-row';
  let applying = false;
  let refreshTimer = null;

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  function getBundleId(item) {
    return item?.properties?._bundleId ? String(item.properties._bundleId) : '';
  }

  function getStoredMeta(bundleId) {
    try {
      const raw = sessionStorage.getItem(`ftr-loop-bundle-meta:${bundleId}`);
      return raw ? JSON.parse(raw) : {};
    } catch (_) {
      return {};
    }
  }

  function getBundleMeta(bundleId, items) {
    const source = (items || []).find((item) => {
      const p = item?.properties || {};
      return p._bundleProductTitle || p._bundleProductUrl || p._bundleProductImage;
    }) || items?.[0] || {};
    const p = source.properties || {};
    const stored = getStoredMeta(bundleId);

    return {
      title: String(p._bundleProductTitle || stored.title || p._bundleName || 'Custom Bundle').trim(),
      url: String(p._bundleProductUrl || stored.url || '').trim(),
      image: String(p._bundleProductImage || stored.image || '').trim()
    };
  }

  function getSellingPlanName(items) {
    return items.find((item) => item?.selling_plan_allocation?.selling_plan?.name)
      ?.selling_plan_allocation?.selling_plan?.name || '';
  }

  function effectiveLinePrice(item) {
    const qty = Math.max(1, Number(item?.quantity || 1));
    const finalLine = Number(item?.final_line_price || 0);
    const allocationUnit = Number(item?.selling_plan_allocation?.price || 0);
    const allocationLine = allocationUnit > 0 ? allocationUnit * qty : 0;

    if (finalLine > 0 && allocationLine > 0) return Math.min(finalLine, allocationLine);
    return allocationLine || finalLine || 0;
  }

  function compareLinePrice(item) {
    const qty = Math.max(1, Number(item?.quantity || 1));
    const current = effectiveLinePrice(item);
    const compareUnit = Number(item?.selling_plan_allocation?.compare_at_price || 0);
    const compareLine = compareUnit > 0 ? compareUnit * qty : 0;
    const originalLine = Number(item?.original_line_price || item?.compare_at_line_price || 0);
    return Math.max(current, compareLine, originalLine);
  }

  function bundlePrices(items) {
    return (items || []).reduce((out, item) => {
      out.final += effectiveLinePrice(item);
      out.compare += compareLinePrice(item);
      return out;
    }, { final: 0, compare: 0 });
  }

  function groupBundleItems(cart) {
    const groups = new Map();
    (cart?.items || []).forEach((item, index) => {
      const bundleId = getBundleId(item);
      if (!bundleId) return;
      if (!groups.has(bundleId)) groups.set(bundleId, []);
      groups.get(bundleId).push({ item, index });
    });
    return groups;
  }

  function injectStyles() {
    if (document.getElementById('ftr-cart-page-bundle-styles')) return;
    const style = document.createElement('style');
    style.id = 'ftr-cart-page-bundle-styles';
    style.textContent = `
      .cc-main-cart .${GENERATED_CLASS} td {
        padding: 18px 20px !important;
        background: #f7fbf8;
        border: 1px solid rgba(31, 107, 66, .16);
        border-bottom: 0;
        border-radius: 14px 14px 0 0;
      }
      .cc-main-cart .ftr-loop-bundle-header {
        display: flex;
        align-items: center;
        gap: 16px;
      }
      .cc-main-cart .ftr-loop-bundle-header__media {
        flex: 0 0 84px;
        width: 84px;
        line-height: 0;
      }
      .cc-main-cart .ftr-loop-bundle-header__image {
        display: block;
        width: 84px;
        height: 84px;
        object-fit: contain;
        border-radius: 10px;
        background: #fff;
      }
      .cc-main-cart .ftr-loop-bundle-header__main { min-width: 0; flex: 1 1 auto; }
      .cc-main-cart .ftr-loop-bundle-header__eyebrow {
        display: inline-flex;
        margin-bottom: 4px;
        font-size: 11px;
        line-height: 1;
        font-weight: 800;
        letter-spacing: .08em;
        text-transform: uppercase;
        color: #1f6b42;
      }
      .cc-main-cart .ftr-loop-bundle-header__title {
        margin: 0;
        font-size: 17px;
        line-height: 1.25;
        font-weight: 800;
        color: inherit;
      }
      .cc-main-cart .ftr-loop-bundle-header__title a { color: inherit; text-decoration: none; }
      .cc-main-cart .ftr-loop-bundle-header__title a:hover { text-decoration: underline; }
      .cc-main-cart .ftr-loop-bundle-header__meta { margin-top: 5px; font-size: 13px; opacity: .72; }
      .cc-main-cart .ftr-loop-bundle-header__side { flex: 0 0 auto; text-align: right; }
      .cc-main-cart .ftr-loop-bundle-header__total { display: block; font-size: 16px; font-weight: 800; white-space: nowrap; }
      .cc-main-cart .ftr-loop-bundle-header__compare { color: #999; font-weight: 400; font-size: 12px; margin-right: 5px; }
      .cc-main-cart .ftr-loop-bundle-remove {
        margin-top: 5px; padding: 0; border: 0; background: transparent; color: inherit;
        font: inherit; font-size: 12px; text-decoration: underline; text-underline-offset: 3px;
        cursor: pointer; opacity: .72;
      }
      .cc-main-cart .ftr-loop-bundle-remove:hover { opacity: 1; }
      .cc-main-cart tr.cart-item.ftr-loop-bundle-child { background: #fbfdfb; grid-template-columns: 1fr !important; }
      .cc-main-cart tr.cart-item.ftr-loop-bundle-child .cart-item__image { display: none !important; }
      .cc-main-cart tr.cart-item.ftr-loop-bundle-child .cart-item__details,
      .cc-main-cart tr.cart-item.ftr-loop-bundle-child .cart-item__qty,
      .cc-main-cart tr.cart-item.ftr-loop-bundle-child .cart-item__total { grid-column: 1 / 2 !important; }
      .cc-main-cart .ftr-loop-bundle-child .cart-item__qty .flex,
      .cc-main-cart .ftr-loop-bundle-child .cart-item__remove { display: none !important; }
      .cc-main-cart .ftr-loop-bundle-child .cart-item__details { padding-left: 4px; }
      .cc-main-cart .ftr-loop-bundle-child .cart-item__title { font-weight: 700; pointer-events: none; text-decoration: none !important; }
      .cc-main-cart tr.cart-item.ftr-loop-bundle-child > td:first-child { border-left: 1px solid rgba(31, 107, 66, .16); }
      .cc-main-cart tr.cart-item.ftr-loop-bundle-child > td:last-child { border-right: 1px solid rgba(31, 107, 66, .16); }
      .cc-main-cart tr.cart-item.ftr-loop-bundle-child.ftr-loop-bundle-child--last > td { border-bottom: 1px solid rgba(31, 107, 66, .16); }

      @media (max-width: 768px) {
        .cc-main-cart .${GENERATED_CLASS} td { padding: 15px !important; }
        .cc-main-cart .ftr-loop-bundle-header { align-items: flex-start; }
        .cc-main-cart .ftr-loop-bundle-header__media,
        .cc-main-cart .ftr-loop-bundle-header__image { width: 64px; height: 64px; flex-basis: 64px; }
        .cc-main-cart .ftr-loop-bundle-header__title { font-size: 15px; }
        .cc-main-cart .ftr-loop-bundle-header__meta { font-size: 12px; }
        .cc-main-cart .ftr-loop-bundle-header__total { font-size: 14px; }
      }
    `;
    document.head.appendChild(style);
  }

  function clearGenerated(root) {
    root.querySelectorAll(`.${GENERATED_CLASS}`).forEach((node) => node.remove());
    root.querySelectorAll('.ftr-loop-bundle-child').forEach((row) => {
      row.classList.remove('ftr-loop-bundle-child', 'ftr-loop-bundle-child--last');
      row.removeAttribute('data-ftr-loop-bundle-id');
    });
  }

  async function removeBundle(bundleId, button) {
    button.disabled = true;
    button.textContent = 'Removing…';
    try {
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      if (!cartResponse.ok) throw new Error('Unable to load cart.');
      const updates = {};
      (cart.items || []).forEach((item) => {
        if (getBundleId(item) === bundleId) updates[item.key] = 0;
      });
      if (!Object.keys(updates).length) return;
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error('Unable to remove bundle.');
      window.location.reload();
    } catch (error) {
      console.error(PREFIX, error);
      button.disabled = false;
      button.textContent = 'Remove bundle';
    }
  }

  function createHeaderRow({ bundleId, entries, cart }) {
    const items = entries.map((entry) => entry.item);
    const quantity = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const plan = getSellingPlanName(items);
    const meta = getBundleMeta(bundleId, items);
    const prices = bundlePrices(items);

    const imageHtml = meta.image
      ? `${meta.url ? `<a class="ftr-loop-bundle-header__media" href="${escapeHtml(meta.url)}">` : '<span class="ftr-loop-bundle-header__media">'}<img class="ftr-loop-bundle-header__image" src="${escapeHtml(meta.image)}" alt="${escapeHtml(meta.title)}" loading="lazy">${meta.url ? '</a>' : '</span>'}`
      : '';
    const titleHtml = meta.url
      ? `<a href="${escapeHtml(meta.url)}">${escapeHtml(meta.title)}</a>`
      : escapeHtml(meta.title);
    const totalHtml = prices.compare > prices.final
      ? `<span class="ftr-loop-bundle-header__compare"><s>${escapeHtml(formatMoney(prices.compare, cart.currency))}</s></span>${escapeHtml(formatMoney(prices.final, cart.currency))}`
      : escapeHtml(formatMoney(prices.final, cart.currency));

    const row = document.createElement('tr');
    row.className = GENERATED_CLASS;
    row.dataset.bundleId = bundleId;
    row.innerHTML = `
      <td colspan="4">
        <div class="ftr-loop-bundle-header">
          ${imageHtml}
          <div class="ftr-loop-bundle-header__main">
            <span class="ftr-loop-bundle-header__eyebrow">Bundle</span>
            <h3 class="ftr-loop-bundle-header__title">${titleHtml}</h3>
            <div class="ftr-loop-bundle-header__meta">${quantity} item${quantity === 1 ? '' : 's'}${plan ? ` · ${escapeHtml(plan)}` : ''}</div>
          </div>
          <div class="ftr-loop-bundle-header__side">
            <span class="ftr-loop-bundle-header__total">${totalHtml}</span>
            <button type="button" class="ftr-loop-bundle-remove" data-remove-loop-bundle="${escapeHtml(bundleId)}">Remove bundle</button>
          </div>
        </div>
      </td>
    `;

    row.querySelector('[data-remove-loop-bundle]')?.addEventListener('click', (event) => {
      removeBundle(bundleId, event.currentTarget);
    });
    return row;
  }

  async function applyBundlePresentation() {
    if (applying) return;
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root || root.dataset.empty === 'true') return;
    applying = true;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js?_=${Date.now()}`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      if (!response.ok) throw new Error('Unable to load cart for bundle grouping.');

      clearGenerated(root);
      const groups = groupBundleItems(cart);
      if (!groups.size) return;

      groups.forEach((entries, bundleId) => {
        const rows = entries.map(({ index }) => document.getElementById(`cart-item-${index + 1}`)).filter(Boolean);
        if (!rows.length) return;

        rows.forEach((row, rowIndex) => {
          row.classList.add('ftr-loop-bundle-child');
          row.dataset.ftrLoopBundleId = bundleId;
          if (rowIndex === rows.length - 1) row.classList.add('ftr-loop-bundle-child--last');
        });

        rows[0].parentNode.insertBefore(createHeaderRow({ bundleId, entries, cart }), rows[0]);
      });

      console.log(PREFIX, `Grouped ${groups.size} Loop bundle${groups.size === 1 ? '' : 's'}.`);
    } catch (error) {
      console.error(PREFIX, error);
    } finally {
      applying = false;
    }
  }

  function scheduleRefresh(delay = 60) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(applyBundlePresentation, delay);
  }

  function init() {
    injectStyles();
    scheduleRefresh(0);
    document.addEventListener('on:line-item:change', () => scheduleRefresh(150));
    document.addEventListener('on:cart:add', () => scheduleRefresh(150));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
